import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { track } from "../../lib/utm";

const WIDE = "/marketing/nexto-anthem-2026-16x9-15s.mp4", TALL_SRC = "/marketing/nexto-anthem-2026-9x16-15s.mp4";
// Phones get a 720p re-encode (~⅓ the bytes); Save-Data or 2G/3G connections get the 540p lite cut.
const conn = typeof navigator !== "undefined" ? navigator.connection : null;
const slowNet = !!conn && (conn.saveData || /(^|-)2g|3g/.test(conn.effectiveType || ""));
const TALL = slowNet ? "/marketing/nexto-anthem-2026-9x16-15s-lite.mp4" : "/marketing/nexto-anthem-2026-9x16-15s-mobile.mp4";

// Autoplaying muted 15s anthem bumper behind the landing hero, with a tap-for-sound toggle.
// Analytics: hero_view (first frame plays, once per session), hero_sound_on (each tap for sound),
// hero_complete (a sound-on play reaches the end of the loop).
export default function HeroFilm() {
  const wideRef = useRef(null), tallRef = useRef(null);
  const [sound, setSound] = useState(false);
  const completeArmed = useRef(false);
  const active = () => (window.matchMedia("(min-width: 640px)").matches ? wideRef.current : tallRef.current);
  useEffect(() => { [wideRef.current, tallRef.current].forEach((v) => v?.play?.().catch(() => {})); }, []);
  const onPlaying = (e) => { if (e.currentTarget === active()) track("hero_view", { once: true }); };
  const onTime = (e) => {
    const v = e.currentTarget;
    if (!completeArmed.current || v !== active() || !v.duration) return;
    if (v.currentTime >= v.duration - 0.35) { completeArmed.current = false; track("hero_complete"); }
  };
  const toggle = () => {
    const v = active(); if (!v) return;
    const next = !sound;
    [wideRef.current, tallRef.current].forEach((x) => { if (x) x.muted = true; });
    v.muted = !next;
    if (next) { v.currentTime = 0; v.play().catch(() => {}); track("hero_sound_on"); completeArmed.current = true; }
    else completeArmed.current = false;
    setSound(next);
  };
  const cls = `w-full h-full object-cover object-center transition-opacity duration-500 ${sound ? "opacity-90" : "opacity-55"}`;
  return (
    <>
      <video ref={wideRef} data-testid="hero-film-wide" poster="/marketing/nexto-anthem-2026-16x9-15s-poster.jpg" autoPlay muted loop playsInline preload="auto"
        onPlaying={onPlaying} onTimeUpdate={onTime} className={`hidden sm:block ${cls}`}>
        <source src={WIDE} type="video/mp4" /><source src={WIDE.replace(".mp4", ".webm")} type="video/webm" />
      </video>
      <video ref={tallRef} data-testid="hero-film-tall" poster="/marketing/nexto-anthem-2026-9x16-15s-poster.jpg" autoPlay muted loop playsInline preload="auto"
        onPlaying={onPlaying} onTimeUpdate={onTime} className={`sm:hidden ${cls}`}>
        <source src={TALL} type="video/mp4" /><source src={TALL_SRC.replace(".mp4", ".webm")} type="video/webm" />
      </video>
      <button data-testid="hero-sound-toggle" onClick={toggle} aria-pressed={sound} aria-label={sound ? "Mute film" : "Play film with sound"}
        className="absolute z-30 top-[calc(4rem+env(safe-area-inset-top,0px)+12px)] right-3 md:top-auto md:bottom-8 md:right-8 inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.25em] backdrop-blur-md transition-colors"
        style={{ borderColor: sound ? "#D4AF37" : "rgba(255,255,255,0.25)", background: sound ? "rgba(212,175,55,0.2)" : "rgba(0,0,0,0.55)", color: sound ? "#D4AF37" : "rgba(255,255,255,0.8)", boxShadow: sound ? "0 0 18px rgba(212,175,55,0.45)" : "none" }}>
        {sound ? <Volume2 size={14} /> : <VolumeX size={14} />} {sound ? "Sound on" : "Tap for sound"}
      </button>
    </>
  );
}
